import { DateTime } from "luxon";
import moment from "moment";
import { ITimeRangeProps } from "../../components/add-time-range/add-time-range";
import { Option } from "../../oversight-core/ui-elements/app-select/app-select";

const mutateDateToCurrentTimeZoneISOString = (date: Date) => {
  return moment(date).toISOString(true);
};

export const formatTime = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;

  return `${formattedHours < 10 ? "0" : ""}${formattedHours}:${
    minutes < 10 ? "0" : ""
  }${minutes} ${period}`;
};

export const formatTimeSlots = (date: Date, timeList: ITimeRangeProps[]) => {
  if (
    timeList[timeList.length - 1].startTime.value &&
    timeList[timeList.length - 1].endTime.value
  ) {
    const tempTimeList = timeList.map((timeRange) => {
      const startTime = timeRange.startTime.value.split(" ");
      const endTime = timeRange.endTime.value.split(" ");
      const startTimeHour = startTime[0].split(":");
      const endTimeHour = endTime[0].split(":");
      let from = "";
      let to = "";
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      if (startTime[1] === "AM" && startTimeHour[0] === "12") {
        from = mutateDateToCurrentTimeZoneISOString(
          new Date(year, month, day, 0, +startTimeHour[1]),
        );
      } else if (startTime[1] === "PM" && startTimeHour[0] !== "12") {
        from = mutateDateToCurrentTimeZoneISOString(
          new Date(year, month, day, +startTimeHour[0] + 12, +startTimeHour[1]),
        );
      } else {
        from = mutateDateToCurrentTimeZoneISOString(
          new Date(year, month, day, +startTimeHour[0], +startTimeHour[1]),
        );
      }

      if (endTime[1] === "AM" && endTimeHour[0] === "12") {
        to = mutateDateToCurrentTimeZoneISOString(
          new Date(year, month, day, 0, +endTimeHour[1]),
        );
      } else if (endTime[1] === "PM" && endTimeHour[0] !== "12") {
        to = mutateDateToCurrentTimeZoneISOString(
          new Date(
            year,
            month,
            day,
            +endTimeHour[0] + 12,
            endTimeHour[1] === "59" ? 60 : +endTimeHour[1],
          ),
        );
      } else {
        to = mutateDateToCurrentTimeZoneISOString(
          new Date(
            year,
            month,
            day,
            +endTimeHour[0],
            endTimeHour[1] === "59" ? 60 : +endTimeHour[1],
          ),
        );
      }
      return { fromDate: from, toDate: to };
    });
    return tempTimeList;
  }
  return [];
};

export const generateTimeRangeList = (
  startTime: Date,
  endTime: Date,
  intervalMinutes: number,
) => {
  const TimeRangeList: Option[] = [];

  const currentTime = new Date(startTime);
  while (currentTime <= endTime) {
    const timeString = currentTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    TimeRangeList.push({
      label:
        moment(currentTime).format("YYYY-MM-DD") ===
          moment(endTime).format("YYYY-MM-DD") && timeString === "12:00 AM"
          ? "12:00 AM"
          : timeString,
      value:
        moment(currentTime).format("YYYY-MM-DD") ===
          moment(endTime).format("YYYY-MM-DD") && timeString === "12:00 AM"
          ? "11:59 PM"
          : timeString,
    });
    currentTime.setMinutes(currentTime.getMinutes() + intervalMinutes);
  }

  return TimeRangeList;
};

export const convertListToTimeZone = (
  timestamps: string[],
  targetTimeZone: string,
): string[] => {
  const [hoursStr, minutesStr] = targetTimeZone.split(":");
  const timeZoneOffset =
    (parseInt(hoursStr) * 60 + parseInt(minutesStr)) * 60 * 1000;

  const convertedTimestamps: string[] = [];

  timestamps.forEach((timestamp) => {
    const date = new Date(timestamp);
    const convertedDate = new Date(date.getTime() + timeZoneOffset);
    const convertedTimestamp = convertedDate
      .toISOString()
      .replace("Z", `${hoursStr}:${minutesStr}`);
    convertedTimestamps.push(convertedTimestamp);
  });

  return convertedTimestamps;
};

export const convertToTimeZone = (
  timestamp: string,
  targetTimeZone: string,
): string => {
  const [hoursStr, minutesStr] = targetTimeZone.split(":");
  const timeZoneOffset =
    (parseInt(hoursStr) * 60 + parseInt(minutesStr)) * 60 * 1000;

  const date = new Date(timestamp);
  const convertedDate = new Date(date.getTime() + timeZoneOffset);
  const convertedTimestamp = convertedDate
    .toISOString()
    .replace("Z", `${hoursStr}:${minutesStr}`);

  return convertedTimestamp;
};

export const formatHour = (hour: number) => {
  if (hour >= 1 && hour <= 11) {
    return `${hour}.00 AM`;
  } else if (hour === 12) {
    return `${hour}.00 PM`;
  } else if (hour >= 13 && hour <= 23) {
    return `${hour}.00 PM`;
  } else if (hour === 24) {
    return `12.00 AM`;
  }
  return "";
};

export const convertToMinutes = (hours: number, minutes: number): number => {
  return hours * 60 + minutes;
};

export const mergeDateAndTime = (
  selectedDate: Date,
  selectedTime: string,
): Date => {
  const [hours, minutes] = selectedTime.split(":").map(Number);

  const mergedDate = new Date(selectedDate);
  mergedDate.setHours(hours, minutes, 0, 0);

  return mergedDate;
};

export const formatMinutesToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export const getHoursAndMinutesSeparately = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return { hours, remainingMinutes };
};

export function getRelativeTimeFromTimestamp(
  epochSeconds: number | string
): string {
  let target: number;

  if (typeof epochSeconds === "string") {
    const parsed = Number(epochSeconds);
    if (!isNaN(parsed)) {
      target = parsed * 1000;
    } else {
      // Try parsing as a date string
      const date = new Date(epochSeconds);
      if (!isNaN(date.getTime())) {
        target = date.getTime();
      } else {
        return "-";
      }
    }
  } else {
    target = epochSeconds * 1000;
  }

  if (!isFinite(target)) return "-";

  const now = Date.now();
  const diffMs = target - now;

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const seconds = diffMs / 1000;

  if (Math.abs(seconds) < 60) {
    const value = Math.round(seconds);
    return isFinite(value) ? rtf.format(value, "second") : "-";
  }

  const minutes = seconds / 60;
  if (Math.abs(minutes) < 60) {
    const value = Math.round(minutes);
    return isFinite(value) ? rtf.format(value, "minute") : "-";
  }

  const hours = minutes / 60;
  if (Math.abs(hours) < 24) {
    const value = Math.round(hours);
    return isFinite(value) ? rtf.format(value, "hour") : "-";
  }

  const days = hours / 24;
  if (Math.abs(days) < 30) {
    const value = Math.round(days);
    return isFinite(value) ? rtf.format(value, "day") : "-";
  }

  const months = days / 30.44;
  if (Math.abs(months) < 12) {
    const value = Math.round(months);
    return isFinite(value) ? rtf.format(value, "month") : "-";
  }

  const years = days / 365.25;
  const value = Math.round(years);
  return isFinite(value) ? rtf.format(value, "year") : "-";
}

export function formatEpochToDate(
  epochSeconds: number,
  zoneId = "Etc/UTC",
): string {
  return DateTime.fromSeconds(epochSeconds)
    .setZone(zoneId)
    .toFormat("yyyy/MM/dd HH:mm:ss");
}

export function getTimestampDuration(
  startEpoch: number,
  endEpoch: number,
): string {
  let diff = Math.abs(endEpoch - startEpoch); // in seconds

  const days = Math.floor(diff / 86400);
  diff %= 86400;
  const hours = Math.floor(diff / 3600);
  diff %= 3600;
  const minutes = Math.floor(diff / 60);
  const seconds = diff % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || parts.length > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return parts.join(", ");
}
